import { pageTitle } from '@palaccount/config';
import { Money } from '@palaccount/money';
import {
  SALES_MESSAGES,
  SEE_ITEM_COST,
  type InvoiceResponse,
  type TwoWayAmount,
} from '@palaccount/contracts';
import type { SalesInvoiceData } from '@palaccount/documents';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, CreditCard, Loader2, Plus, Send, Trash2, TriangleAlert, Undo2, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';

import { api } from '@/api';
import { Amount, Ltr } from '@/components/amount';
import { formatQtyWithUnit } from '@/lib/format';
import { ConfirmBox, DeleteConfirmBox } from '@/components/confirm-box';
import { ReasonBox } from '@/components/reason-box';
import { Gated } from '@/components/permission-gate';
import { DocumentNumberChip } from '@/components/document-number-chip';
import { useUnsavedChanges } from '@/components/document-header-controls';
import { DocumentToolbar } from '@/components/document-toolbar';
import { DocStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { DocumentActions } from '@/features/documents/document-actions';
import { useLineImport } from '@/features/documents/line-tools';
import { DocumentTotals, TwoWayPair } from '@/features/documents/document-totals';
import { usePriceTiers } from '@/features/inventory/api';
import { LastPriceHint } from '@/features/documents/last-price-hint';
import { DescriptionLangToggle, useDescriptionLanguage } from '@/features/documents/description-language';
import { useRateWarningGate } from '@/features/documents/fx-panel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { ItemCell } from '@/features/inventory/item-cell';
import { useItemPicker } from '@/features/inventory/item-picker';
import { useWarehouses } from '@/features/inventory/stock-api';
import { PartnerBalanceChip } from '@/features/partners/partner-balance-chip';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { useOrgSetting } from '@/features/admin/api';
import { useInvoiceCompliance } from '@/features/einvoicing/api';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import {
  INVOICES_KEY,
  useCancelInvoice,
  useCreateInvoice,
  useDeleteInvoice,
  useInvoice,
  usePostInvoice,
  useUpdateInvoice,
} from './api';
import { ComplianceStrip } from './compliance-strip';
import { CopyFromModal, type CopiedDocument } from './copy-from-modal';
import { BelowCostHint, LineStockHint, OverStockStrip, belowCost, shortBy } from './line-stock-warning';
import {
  LineUnitSelect,
  SalesHeaderFields,
  salesHeaderCreateBody,
  salesHeaderFromDoc,
  salesHeaderPatchBody,
  useLineColumns,
  useSalesHeaderState,
} from './sales-header-fields';
import { useRateInput } from '@/features/documents/rate-field';
import { useDocCurrency } from '@/features/documents/doc-currency';
import { CollapsibleHeaderCard } from '@/features/documents/header-card';
import { useCommissionInput } from '@/features/sales/commission-field';
import {
  CashInvoiceBanner,
  CashInvoiceFields,
  cashInvoiceCreateBody,
  cashInvoicePatchBody,
  type CashInvoiceState,
} from './cash-invoice-fields';
import { applicableTierId, tierColumns, tierPriceFor } from './quote-tiers';
import { MarginPanel, netAmount } from './margin-panel';
import { computeTotalsMirror, type TwoWayState } from './totals-mirror';
import {
  captureBlocks,
  textFromStored,
  trackingPayload,
  type CaptureText,
  type TrackedItem,
} from './tracking-capture';
import { TrackingRows } from './tracking-rows';

interface GridLine {
  key: number;
  /** Item-linked or free text (R14 pragmatism) — a DIRECT product line moves stock at posting. */
  itemId: string;
  warehouseId: string;
  /** INV-L4: the unit the qty is typed in; '' = the item's stock unit. */
  unitId: string;
  description: string;
  qty: string;
  /** INV-L1: the free quantity beside qty; '' = none. */
  bonusQty: string;
  unitPrice: string;
  discountPct: string;
  /**
   * OQ-05-17's sub-table, per block: the line's own identifiers under
   * `·self`, a kit component's under its item id. A DIRECT invoice moves
   * stock at posting, so this is where a serial-tracked line — or a kit whose
   * part is tracked (R7) — says which units are leaving.
   */
  tracking: CaptureText;
  /**
   * BR-MD-03-14's "already typed", as a fact about the CELL rather than about
   * its contents: set by the cell's own `onChange` and by nothing else.
   *
   * This is `partners/partner-form-page.tsx`'s `dirtyFields` test, ported by
   * hand because a line grid keeps its rows in `useState` and has no
   * react-hook-form to ask. That screen's note says why the shape matters in
   * its own words — "THE TEST IS 'HAS THE USER TOUCHED IT', NOT 'IS IT STILL
   * EMPTY'" — and a grid has the same two cases plus one of its own: a price
   * typed as `0`, a description typed and then deliberately cleared, and text
   * a user typed that happens to equal the item's own name. An emptiness test
   * loses the first two; a "does it still hold what the pick put there" test
   * loses the third, and loses it on the SECOND pick, where nobody is looking.
   *
   * Lines loaded from a saved document arrive with both set: what is on a
   * saved line was typed by a person and committed. The cost of that reading
   * is friction; the cost of the other one is a negotiated price disappearing
   * without a word.
   */
  typedDescription: boolean;
  typedUnitPrice: boolean;
  /**
   * BR-MD-12-10 / GEN-120's "must show both": the STORED stock-unit twin of a
   * line typed in another unit — "2 BOX (1,000 pcs)" — carried only while the
   * stored conversion is still true of the cell. Present exactly when the
   * server sent `stockUnitSymbol` (a line whose unit differs from the item's
   * stock unit); cleared the moment the qty is retyped or the item swapped,
   * because the pair is the SERVER's conversion of the saved figure and a
   * twin left beside an edited quantity would caption a number it no longer
   * describes. Saving recomputes it and the reload brings it back.
   */
  basePair: { qty: string; symbol: string; precision: number } | null;
}

let nextKey = 1;
const blankLine = (): GridLine => ({
  key: nextKey++,
  itemId: '',
  warehouseId: '',
  unitId: '',
  description: '',
  qty: '',
  bonusQty: '',
  unitPrice: '',
  discountPct: '',
  tracking: {},
  typedDescription: false,
  typedUnitPrice: false,
  // A fresh line has no unit picker, so its qty IS the stock unit's (the
  // server stores unit_id NULL) and there is no twin to show.
  basePair: null,
});

const MONEY_RE = /^\d{1,13}(\.\d{1,2})?$/;
const PCT_RE = /^\d{1,3}(\.\d{1,2})?$/;
const QTY_RE = /^\d{1,12}(\.\d{1,3})?$/;

/** INV-L1: a stored bonus of 0.000 is an empty cell, not a zero to read. */
function bonusText(stored: string | undefined): string {
  // Tolerates a line written before the column existed (and older fixtures).
  if (stored === undefined || /^0+(\.0+)?$/.test(stored)) return '';
  return stored.includes('.') ? stored.replace(/\.?0+$/, '') : stored;
}

/**
 * PART 4's form conventions, dispositioned at this exemplar document form:
 * GEN-40/GEN-40b/GEN-40d (owner ruling R3) — each document type is its own
 * screen with its own route and labels written for its one type, and the
 * six share §4.7/§4.8/§4.9 as conventions, not a screen; GEN-33 one record
 * per form; GEN-37 the number field is read-only text, never typed; GEN-38
 * related documents link by their own printed number; GEN-57 Save never
 * changes status (posting is its own route and its own verb). REPORTED,
 * not built: GEN-34's record-stepping toolbar (the R3 toolbar has no
 * |</< >/>| rail), GEN-35's leave-with-unsaved-typing prompt, GEN-39's
 * three party-side buttons, GEN-47's required-field asterisks.
 *
 * `InvoiceResponse` → the printable document, for the INSTANT local print.
 *
 * A straight field mapping and nothing more. The PDF the worker produces does
 * NOT come through here — it re-reads the invoice from the database — so this
 * is the browser's copy for paper, and the server's copy for a file, both fed
 * into the same template. What is deliberately not here is arithmetic: every
 * figure is the stored PART 18 block, exactly as the screen shows it.
 */
function invoiceDocument(
  invoice: InvoiceResponse,
  warehouses: readonly { id: string; name: string }[] = [],
): SalesInvoiceData {
  // INV-F1 / INV-L3 for `print.show_warehouse`: the header's name comes
  // resolved on the response; a line's own override is an id, named off the
  // warehouse list this screen already holds (absent → the template falls
  // back to the header's).
  const warehouseName = (id: string | null): string | null =>
    id === null ? null : (warehouses.find((warehouse) => warehouse.id === id)?.name ?? null);
  return {
    code: invoice.code,
    status: invoice.status,
    invoiceDate: invoice.invoiceDate,
    dueDate: invoice.dueDate,
    partnerCode: invoice.partnerCode,
    partnerName: invoice.partnerName,
    currency: invoice.currency,
    warehouseName: invoice.warehouseName,
    lines: invoice.lines.map((line) => ({
      lineNo: line.lineNo,
      itemCode: line.itemCode,
      description: line.description,
      warehouseName: warehouseName(line.warehouseId),
      qty: line.qty,
      // N-4: the unit the quantity is in, straight off the response — see
      // InvoiceLineSchema. Null on a free-text line, which has no unit.
      unit:
        line.unitSymbol === null || line.unitPrecision === null
          ? null
          : { symbol: line.unitSymbol, precision: line.unitPrecision },
      unitPrice: line.unitPrice,
      discountPct: line.discountPct,
      lineTotal: line.lineTotal,
    })),
    subtotal: invoice.subtotal,
    discountPct: invoice.discountPct,
    discountAmount: invoice.discountAmount,
    taxPct: invoice.taxPct,
    taxAmount: invoice.taxAmount,
    grandTotal: invoice.grandTotal,
    amountPaid: invoice.amountPaid,
    balanceDue: invoice.balanceDue,
    notes: invoice.notes,
  };
}

function fromInvoice(invoice: InvoiceResponse): GridLine[] {
  return invoice.lines.map((line) => ({
    key: nextKey++,
    itemId: line.itemId ?? '',
    warehouseId: line.warehouseId ?? '',
    unitId: line.unitId ?? '',
    description: line.description,
    qty: line.qty,
    bonusQty: bonusText(line.bonusQty),
    unitPrice: line.unitPrice,
    discountPct: line.discountPct,
    tracking: textFromStored(line),
    // A saved line's text and price are somebody's typed work, so picking a
    // different item on it does not throw them away. New lines added to this
    // document are blank and fill normally.
    typedDescription: true,
    typedUnitPrice: true,
    // BR-MD-12-10: the stock-unit twin, present exactly when the server
    // resolved one (a line typed in a non-stock unit).
    basePair:
      line.baseQty !== null && line.stockUnitSymbol !== null && line.stockUnitPrecision !== null
        ? { qty: line.baseQty, symbol: line.stockUnitSymbol, precision: line.stockUnitPrecision }
        : null,
  }));
}

/** A grid cell input: compact, monospace where figures live. */
function CellInput({
  mono,
  className,
  ...props
}: React.ComponentProps<'input'> & { mono?: boolean }) {
  return (
    <input
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-sm outline-none transition-colors',
        'hover:border-input focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-70',
        mono && 'text-end font-mono tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

/**
 * SAL-04 on the SAL-00 shared layout — the slice's subset: the details line,
 * the line grid (free-text lines until items land — the contract's own TODO),
 * the payment-less summary with both two-way pairs, and Post behind
 * `sales.approve`. A POSTED invoice is read-only except its notes
 * (BR-SAL-00-22, ruling R8).
 *
 * ═══ THE SAL-00 QUEUE, AS THIS SCREEN RECORDS IT ═════════════════════════
 * Every id below carries the scanner's ignore mark on its own line, so this
 * paragraph does not count as naming any of them. The salescluster wave
 * wrote it as six rules deliberately not cited; the sal00 wave then took
 * three of the six to real citations at their enforcing lines, so three
 * entries are now pointers and two of the remaining three are retirements.
 *
 *   BR-SAL-00-04 — rule-coverage:ignore — and
 *   BR-SAL-00-25 — rule-coverage:ignore — are RETIRED BY THE SPEC ITSELF
 *     (owner rulings R3 and R1, §6.7 and §6.10). One removes the
 *     document-type dropdown; the other removes the Easy/Advanced view mode.
 *     Neither describes anything a build can enforce — both sit on the
 *     retired list (`retired-rule-ids.mjs`), out of the queue's denominator.
 *     R1's surviving intent ("two users with different permissions who
 *     enter the same document must produce the same stored record") IS the
 *     build's model — a permission gates the ACT, never the value, which is
 *     BR-PERM-18/-21's greying — but that sentence is not the rule's text.
 *
 *   BR-SAL-00-02 — rule-coverage:ignore — NOW CITED at the shared chip all
 *     five toolbars render, `components/document-number-chip.tsx`: the
 *     never-switches half is owner ruling R3 by construction, and the
 *     naming half is the chip's REQUIRED label, visually hidden so the
 *     number is never announced bare. Whether the number should ALSO carry
 *     a VISIBLE label is still the owner's screen question, recorded at the
 *     component — an sr-only label neither decides nor forecloses it.
 *     Guard: `src/test/sal00-number-label.test.tsx`.
 *
 *   BR-SAL-00-17 — rule-coverage:ignore — NOW CITED at the two
 *     `validateAllocations` (`receipts.service.ts`, customer side;
 *     `payments.service.ts`, supplier side), and its §14 row
 *   ERR-SAL-00-05 — rule-coverage:ignore — at `RECEIPT_MESSAGES.overAllocated`
 *     (`packages/contracts/src/receipts.ts`). Both behavioural clauses hold
 *     in the FIN-03/FIN-04 model: no negative amount can exist
 *     (`MoneyStringSchema` admits no sign; `receipt_allocations` carries
 *     `CHECK (amount > 0)`), and money applied to a document is capped at
 *     its balance due, so payments can never total more than the grand
 *     total — the excess stays customer credit, which is the spec message's
 *     own remedy ("record the extra as an advance", BR-FIN-03-09). This
 *     screen still has NO payment section — SAL-00 §4's payment card is a
 *     Receipt document here — and whether that card is ever built onto the
 *     editor stays the owner's; the rule's substance does not wait on it.
 *
 *     (This sentence used to repeat the row's id and the SECOND mention
 *     carried no mark, so the scanner counted the paragraph that refuses to
 *     cite it as a citation. Caught by the per-family delta, which showed a
 *     -5 where a -4 was intended; the pass/fail could not have shown it. The
 *     mark is per LINE, so every line that names an id needs its own.)
 *
 *   BR-SAL-00-21 — rule-coverage:ignore — "hiding a column must never change
 *     any total." RETIRED, D57: §5b's settings panel and its
 *     seven per-user column switches are not built, and no DOCUMENT grid has
 *     a column toggle. (The reports detail table grew one —
 *     `reports/column-picker.ts`, BR-DSH-10-24's territory — which is a
 *     different screen family under its own rules; the salescluster wave's
 *     "no grid in the product" was true when written and is corrected here.)
 *     The property also cannot fail structurally today: totals are computed
 *     by the server and mirrored from row STATE (`totals-mirror.ts`), never
 *     read off rendered cells, so no visibility switch could reach them.
 *     Citing a column-hiding rule where no column can be hidden would be the
 *     vacuous kind, and delegated ruling D57 (30 Aug 2026) retired the rule
 *     reversibly on exactly that ground — it leaves the queue by a "Retired
 *     by delegated ruling D57" notice at its own spec declaration
 *     (finalnotices--spec.patch), not by a citation here, so the mark stays.
 *     Reversal, per the notice: §5b landing, at which point the toggle is
 *     built over the server-computed totals and the rule is cited there.
 */
export function InvoiceEditorPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isNew = !invoiceId;

  const { data: invoice, isPending: loadingInvoice } = useInvoice(invoiceId);
  const { data: warehouses } = useWarehouses();
  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice(invoiceId ?? '');
  const deleteInvoice = useDeleteInvoice(invoiceId ?? '');
  const postInvoice = usePostInvoice(invoiceId ?? '');
  const cancelInvoice = useCancelInvoice(invoiceId ?? '');

  // These three still answer FIELD and BANNER questions — BR-PERM-21 wants a
  // document a person may not change to be genuinely inert, not merely grey.
  // Every BUTTON question moved to <Gated>, which greys (BR-PERM-18).
  const canEdit = useCan(isNew ? 'sales.create' : 'sales.edit');
  const canPost = useCan('sales.approve');
  const canEditNotes = useCan('sales.edit');

  const [partnerId, setPartnerId] = useState(searchParams.get('partnerId') ?? '');
  const [invoiceDate, setInvoiceDate] = useDocumentDate();
  const [dueDate, setDueDate] = useState('');
  /** INV-F1…F6 — the shared sales header; a new document's salesperson is me. */
  const [header, setHeader] = useSalesHeaderState(isNew);
  // Wave 4D — the editable rate (features/documents/rate-field).
  const rate = useRateInput(invoice);
  // Wave 4D — the commission % (features/sales/commission-field).
  const commission = useCommissionInput(invoice);
  // Wave 4D — the cash invoice (features/sales/cash-invoice-fields).
  const [cash, setCash] = useState<CashInvoiceState>({ isCash: false, cashAccountId: '' });
  /** INV-L1 / INV-L2 — whether this company draws the Bonus and Disc. columns. */
  const { bonusOn, discountOn } = useLineColumns();
  /** G-10 — the last write's non-blocking warnings (duplicate invoice). */
  const [warnings, setWarnings] = useState<string[]>([]);
  const [lines, setLines] = useState<GridLine[]>([blankLine()]);
  const [discount, setDiscount] = useState<TwoWayState | undefined>(undefined);
  const [tax, setTax] = useState<TwoWayState | undefined>(undefined);
  const [notes, setNotes] = useState('');
  // A refusal raised while the invoice was still /sales/new arrives through
  // the router's state, because the screen moves onto the draft it created.
  const [error, setError] = useState<string | null>(
    (location.state as { postError?: string } | null)?.postError ?? null,
  );
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** Ruling A6's ask. GEN-59: the box IS the confirm, and it types a reason. */
  const [cancelAsk, setCancelAsk] = useState(false);
  /** Wave 1B: "This invoice totals 0. Save anyway?" — see `onSave`. */
  const [zeroAsk, setZeroAsk] = useState(false);
  /** BR-MD-11-14's question, in ADM-02 §8's shared box (BR-PERM-15). */
  const { confirmRateWarning, rateWarningDialog } = useRateWarningGate();
  const loadedId = useRef<string | null>(null);
  /** INV-H7 — the "Copy from…" picker, offered on a NEW invoice only. */
  const [copyOpen, setCopyOpen] = useState(false);
  /** INV-H7 — the sentence the last copy left. */
  const [importNote, setImportNote] = useState<string | null>(null);
  /** S4 — the header rail's ‹‹ ›› wait while there is typed work (see `useUnsavedChanges`). */
  const dirty = useUnsavedChanges(
    [partnerId, invoiceDate, dueDate, header, lines, discount, tax, notes],
    isNew ? 'new' : invoice && loadedId.current === invoice.id ? invoice.id : null,
  );

  const status = invoice?.status ?? 'draft';
  /**
   * BR-SAL-04-11's trigger, READ FROM THE E-INVOICING MODULE and never from a
   * column on this document — BR-SAL-04-05, in as many words: the submission
   * status "must be read from the E-Invoicing module, never copied onto the
   * invoice, so the two can never disagree."
   *
   * This is the same hook the compliance strip below already calls with the
   * same argument, so it costs no second request — react-query serves both
   * from one cache entry. Undefined while the fetch is in flight, which reads
   * as "not accepted" and therefore as the QUIETER prompt: a warning that
   * flickers in after the dialog opens would be worse than one that waits for
   * the fact.
   */
  const { data: complianceRecord } = useInvoiceCompliance(
    invoiceId !== undefined && status === 'posted' ? invoiceId : undefined,
  );
  const authorityAccepted = complianceRecord?.status === 'accepted';
  /** PART 3: the document prints in the language the reader is reading in. */
  const printLocale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);
  const isDraft = status === 'draft';
  const editable = isDraft && canEdit;

  useEffect(() => {
    document.title = pageTitle(t('sales.editorTitle'));
  }, [t]);

  // Adopt the server's stored document wholesale — its totals are the truth.
  useEffect(() => {
    if (invoice && loadedId.current !== invoice.id) {
      loadedId.current = invoice.id;
      setPartnerId(invoice.partnerId);
      setInvoiceDate(invoice.invoiceDate);
      setDueDate(invoice.dueDate);
      setHeader(salesHeaderFromDoc(invoice));
      setCash({ isCash: invoice.isCash, cashAccountId: invoice.cashAccountId ?? '' });
      setLines(fromInvoice(invoice));
      setDiscount({ basis: 'pct', value: invoice.discountPct });
      setTax({ basis: 'pct', value: invoice.taxPct });
      setNotes(invoice.notes ?? '');
    }
  }, [invoice]);

  /*
   * GEN-50 — two typeaheads, not two whole tables. The customer picker asks
   * for one panel of matches; the item picker asks for that plus the items
   * these LINES name, so reopening a saved invoice shows every line's item
   * whether or not it matches what is in the search box.
   */
  const customerPicker = usePartnerPicker(partnerId || null, { type: 'customer' });
  const itemPicker = useItemPicker(
    useMemo(() => lines.map((line) => line.itemId), [lines]),
    { status: 'active' },
  );

  const partner = customerPicker.byId.get(partnerId);
  // GEN-108's chain, ending where it should: the DOCUMENT's own code, else
  // the party's usual currency, else the company's base (GEN-107) read from
  // the session — never a literal, which printed dollars on a euro
  // company's every new document until a party was picked.
  const base = useBaseCurrency();
  /**
   * Wave 4C — BR-SAL-01-05's tier pricing, as the quotation applies it: a
   * picked (or imported) item fills at the price of the tier that applies to
   * THIS customer (theirs, else the company default), the item's unit price
   * as the fallback. A reader whose role cannot list tiers gets the unit price.
   */
  const tiersQuery = usePriceTiers(false);
  const saleTier = useMemo(
    () => applicableTierId(partner?.priceTierId, tierColumns(tiersQuery.data ?? [])),
    [partner, tiersQuery.data],
  );
  // Per-document currency (2026-10-30): the header's dropdown sits in front
  // of the chain above; a pick clears the typed rate (it was the old currency's).
  const docCurrency = useDocCurrency({
    partnerId,
    savedCurrency: invoice?.currency,
    partnerCurrency: partner?.currency ?? base,
    onPick: () => rate.setText(''),
  });
  const currency = docCurrency.currency;
  /** A linked invoice (raised from an order) keeps its source's currency. */
  const currencyLocked = !editable || (invoice?.salesOrderId ?? null) !== null;

  // The live mirror (display only — the server recomputes on every save).
  // EC-19-73: the tax box opens at the COMPANY default VAT rate.
  const defaultTaxPct = useOrgSetting<string>('invoicing.default_vat_pct');
  // MD-09 §9 — off means the Due date field is not drawn at all.
  const paymentTermsOn = useOrgSetting<boolean>('invoicing.payment_terms');
  const totals = useMemo(
    () => computeTotalsMirror(lines, discount, tax, currency, defaultTaxPct),
    [lines, discount, tax, currency, defaultTaxPct],
  );

  const setLine = (key: number, patch: Partial<GridLine>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  /**
   * BR-SAL-00-20: removing the last line leaves one blank line.
   *
   * ERR-SAL-00-08 is the §14 row for this same act — "removed the only line:
   * *(no message — a blank line appears in its place)* · Type into it". It
   * READS like a defect report and is required behaviour, so the absence of a
   * message is deliberate here and not an omission: `removeLine` never sets
   * `error`, and the seeded blank row is what the row means by "in its place".
   */
  const removeLine = (key: number) => {
    setLines((current) => {
      const remaining = current.filter((line) => line.key !== key);
      return remaining.length === 0 ? [blankLine()] : remaining;
    });
  };

  /**
   * SAL-00 §5d keyboard navigation, the slice's subset: Enter/Tab advance,
   * arrows move by row, and moving past the last row appends a blank one so
   * the grid grows by typing.
   */
  const gridRef = useRef<HTMLTableElement | null>(null);
  const focusCell = (row: number, col: number) => {
    const cell = gridRef.current?.querySelector<HTMLInputElement>(
      `input[data-row="${row}"][data-col="${col}"]`,
    );
    cell?.focus();
  };
  const onCellKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, row: number, col: number) => {
    const lastCol = discountOn ? 3 : 2;
    if (event.key === 'Enter') {
      event.preventDefault();
      if (col < lastCol) focusCell(row, col + 1);
      else if (row + 1 < lines.length) focusCell(row + 1, 0);
      else {
        setLines((current) => [...current, blankLine()]);
        requestAnimationFrame(() => focusCell(row + 1, 0));
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (row + 1 < lines.length) focusCell(row + 1, col);
      else {
        setLines((current) => [...current, blankLine()]);
        requestAnimationFrame(() => focusCell(row + 1, col));
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (row > 0) focusCell(row - 1, col);
    }
  };

  const activeWarehouses = (warehouses ?? []).filter((warehouse) => warehouse.isActive);

  /**
   * bugs: Sales › Invoices — "when a quantity is chosen, take the stock in the
   * warehouse into account and WARN before finishing if it is less than what
   * is being sold; the feature exists elsewhere". `line-stock-warning.tsx`
   * has the rule (warn, never block, per item AND line warehouse); this grid
   * adds one exclusion of its own: a line whose qty is in a non-stock unit
   * (`basePair`) is not compared, because the figure in stock is in the stock
   * unit and the typed one is not.
   */
  const lineShort = (line: GridLine): string | null =>
    editable && line.basePair === null && line.unitId === ''
      ? shortBy(itemPicker.byId.get(line.itemId), line.qty, {
          // INV-L3: a line without its own warehouse ships from the header's.
          warehouseId: line.warehouseId || header.warehouseId,
        })
      : null;
  const anyShort = lines.some((line) => lineShort(line) !== null);
  /** INV-L7 — "Below cost (N)" under the price, for a reader allowed to see cost. */
  const canSeeCost = useCan(SEE_ITEM_COST);
  const lineBelowCost = (line: GridLine): string | null =>
    editable && canSeeCost
      ? belowCost(line.unitPrice, itemPicker.byId.get(line.itemId)?.costPrice)
      : null;

  /** BR-MD-03-14: picking fills name and price; it never overwrites typed work. */
  const pickItem = (key: number, itemId: string) => {
    const item = itemPicker.byId.get(itemId);
    if (!item) {
      setLine(key, { itemId: '', warehouseId: '', tracking: {} });
      return;
    }
    setLines((current) =>
      current.map((line) =>
        line.key === key
          ? {
              ...line,
              itemId,
              // The rule's two halves, per FIELD: fill what the user has not
              // touched, and leave what they have. Per field and not per line
              // — a typed description must not stop the price filling.
              description: line.typedDescription ? line.description : descLang.describe(item),
              unitPrice: line.typedUnitPrice ? line.unitPrice : tierPriceFor(item, saleTier),
              // A different item asks for different identifiers, so what was
              // scanned for the old one is thrown away rather than silently
              // re-used. THIS RESET IS NOT WHAT BR-MD-03-14 PROTECTS and it
              // stays unconditional: the rule covers what a person TYPED, and
              // a serial scanned against another item is not typed work about
              // this line — carrying it over would attach one item's units to
              // another item's line, which is worse than losing it.
              tracking: {},
              // BR-MD-12-10: the stock-unit twin belonged to the OLD item's
              // stored conversion — a different item has a different stock
              // unit, so the pair is dropped rather than re-captioned — and
              // so is the picked unit (INV-L4), which was one of the OLD
              // item's units.
              basePair: null,
              unitId: '',
            }
          : line,
      ),
    );
  };

  /**
   * OQ-05-17's blocks for one line: nothing for an untracked item, one block
   * for a tracked one, one per TRACKED COMPONENT when an auto-deducting kit
   * expands (R7 — the kit itself never moves).
   */
  const blocksFor = (line: GridLine) =>
    captureBlocks(
      itemPicker.byId.get(line.itemId) as TrackedItem | undefined,
      line.qty,
    );

  const setTracking = (key: number, blockKey: string, value: string) => {
    setLines((current) =>
      current.map((line) =>
        line.key === key ? { ...line, tracking: { ...line.tracking, [blockKey]: value } } : line,
      ),
    );
  };

  const usedLines = lines.filter(
    (line) => line.description.trim() !== '' || line.qty.trim() !== '' || line.unitPrice.trim() !== '',
  );

  const validate = (): string | null => {
    if (!partnerId) return t('sales.errNoCustomer');
    if (usedLines.length === 0) return t('sales.errNoLines');
    for (const [index, line] of usedLines.entries()) {
      if (line.description.trim() === '') return t('sales.errLineDescription', { line: index + 1 });
      if (!QTY_RE.test(line.qty.trim()) || /^0+(\.0+)?$/.test(line.qty.trim()))
        return t('sales.errLineQty', { line: index + 1 });
      if (!MONEY_RE.test(line.unitPrice.trim())) return t('sales.errLinePrice', { line: index + 1 });
      const bonus = line.bonusQty.trim();
      if (bonusOn && bonus !== '' && !QTY_RE.test(bonus)) return t('sales.errLineBonus', { line: index + 1 });
      const pct = line.discountPct.trim();
      if (discountOn && pct !== '' && !PCT_RE.test(pct)) return t('sales.errDiscountRange');
    }
    if (discount && !(discount.basis === 'pct' ? PCT_RE : MONEY_RE).test(discount.value.trim()))
      return t('sales.errDiscountRange');
    if (tax && !(tax.basis === 'pct' ? PCT_RE : MONEY_RE).test(tax.value.trim()))
      return t('sales.errTaxRange');
    return null;
  };

  const buildTwoWay = (state: TwoWayState | undefined): TwoWayAmount | undefined => {
    if (!state) return undefined;
    return state.basis === 'pct' ? { pct: state.value.trim() } : { amount: state.value.trim() };
  };

  const requestLines = () =>
    usedLines.map((line) => {
      // INV-L3: a line without a warehouse of its own ships from the header's
      // default — resolved HERE, at save, so the stored line says where it
      // actually left from and the per-line override stays a real override.
      const warehouseId = line.warehouseId || header.warehouseId;
      return {
        ...(line.itemId ? { itemId: line.itemId } : {}),
        ...(line.itemId && warehouseId ? { warehouseId } : {}),
        ...(line.itemId && line.unitId ? { unitId: line.unitId } : {}),
        description: line.description.trim(),
        qty: line.qty.trim(),
        // INV-L1: sent only while the column is drawn; off, the line saves 0.
        ...(bonusOn && line.bonusQty.trim() !== '' ? { bonusQty: line.bonusQty.trim() } : {}),
        unitPrice: line.unitPrice.trim(),
        // INV-L2: a hidden Disc. column saves as 0 (the value is not sent).
        ...(discountOn && line.discountPct.trim() !== ''
          ? { discountPct: line.discountPct.trim() }
          : {}),
        // The identifiers travel with the DRAFT, so a half-picked line survives
        // a save and comes back with what was already scanned in it.
        ...trackingPayload(blocksFor(line), line.tracking),
      };
    });

  /**
   * Lines arriving from outside the grid — INV-H7's copy and INV-H1's import
   * — are appended after whatever is already typed, with the blank trailing
   * line dropped first. They carry `typedDescription`/`typedUnitPrice` as a
   * loaded line does: what came off another document or a spreadsheet is
   * somebody's committed figures, not a pick's defaults.
   */
  const appendLines = (
    incoming: ReadonlyArray<{
      itemId: string;
      warehouseId: string;
      unitId: string;
      description: string;
      qty: string;
      bonusQty: string;
      unitPrice: string;
      discountPct: string;
    }>,
  ) => {
    setLines((current) => {
      const kept = current.filter(
        (line) => line.description.trim() !== '' || line.qty.trim() !== '' || line.unitPrice.trim() !== '',
      );
      const added: GridLine[] = incoming.map((line) => ({
        ...blankLine(),
        ...line,
        typedDescription: true,
        typedUnitPrice: true,
      }));
      return [...kept, ...added];
    });
  };

  const onCopyFrom = (copiedDoc: CopiedDocument) => {
    appendLines(copiedDoc.lines);
    if (!partnerId && copiedDoc.partnerId) setPartnerId(copiedDoc.partnerId);
    setImportNote(t('sales.copyFromDone', { lines: copiedDoc.lines.length }));
  };

  /**
   * INV-H1 — Code | Description | Qty | Unit price | Discount %, resolved by
   * item code (wave 4C: the shared line tools; unknown codes stay free text
   * and are listed row by row).
   */
  /** Wave 4C — which name a picked/imported item writes into a NEW line description. */
  const descLang = useDescriptionLanguage();
  const lineImport = useLineImport({
    id: 'invoice',
    side: 'sales',
    describe: descLang.describe,
    price: (item) => tierPriceFor(item, saleTier),
    copyLines: () =>
      lines
        .filter((line) => line.itemId !== '' || line.description.trim() !== '')
        .map((line) => ({
          code: itemPicker.byId.get(line.itemId)?.code ?? '',
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
        })),
    visible: isDraft,
    gate: {
      permission: isNew ? 'sales.create' : 'sales.edit',
      reasonKey: isNew ? 'perm.cannot.createInvoices' : 'perm.cannot.editInvoices',
    },
    disabled: busy,
    onLines: (imported) =>
      appendLines(
        imported.map((line) => ({
          itemId: line.itemId,
          warehouseId: '',
          unitId: '',
          description: line.description,
          qty: line.qty,
          bonusQty: '',
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
        })),
      ),
  });

  const handleFailure = (thrown: unknown, permission: 'sales.create' | 'sales.edit' | 'sales.delete' | 'sales.approve') => {
    if (thrown instanceof ApiError && thrown.status === 403) denyPermission(permission);
    // The line above already reads the cause to spot a 403; this one used to
    // throw it away one expression later. `localizeServerCause` keeps it: a
    // refusal keeps the server's sentence localized (`SALES_MESSAGES` are
    // mapped in STATIC_MESSAGE_KEYS), a request that never answered gets this
    // screen's own sentence rather than the browser's `Failed to fetch`.
    setError(localizeServerCause(t, thrown, 'sales.errSaveFailedInvoice'));
  };

  /**
   * Saves the draft; returns the stored invoice (or null on refusal).
   * `navigateAfter=false` lets Post finish before the detail route mounts —
   * otherwise the fresh page's initial fetch (still a draft) races the post.
   */
  const save = async (navigateAfter = true): Promise<InvoiceResponse | null> => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return null;
    }
    try {
      if (isNew) {
        const created = await createInvoice.mutateAsync({
          partnerId,
          invoiceDate,
          ...(dueDate ? { dueDate } : {}),
          ...salesHeaderCreateBody(header),
          ...docCurrency.body(),
          ...rate.createBody(),
          ...commission.createBody(),
          ...cashInvoiceCreateBody(cash),
          lines: requestLines(),
          ...(discount ? { discount: buildTwoWay(discount)! } : {}),
          ...(tax ? { tax: buildTwoWay(tax)! } : {}),
          ...(notes.trim() ? { notes } : {}),
        });
        setWarnings(created.warnings ?? []);
        if (navigateAfter) void navigate(`/sales/${created.id}`, { replace: true });
        return created;
      }
      const updated = await updateInvoice.mutateAsync({
        invoiceDate,
        ...(dueDate ? { dueDate } : {}),
        ...salesHeaderPatchBody(header),
        ...docCurrency.body(),
        ...rate.patchBody(),
        ...commission.patchBody(),
        ...cashInvoicePatchBody(cash),
        lines: requestLines(),
        ...(discount ? { discount: buildTwoWay(discount)! } : {}),
        ...(tax ? { tax: buildTwoWay(tax)! } : {}),
        notes: notes.trim() === '' ? null : notes,
      });
      setWarnings(updated.warnings ?? []);
      loadedId.current = null; // re-adopt the stored document
      return updated;
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'sales.create' : 'sales.edit');
      return null;
    }
  };

  const saveNow = async () => {
    setZeroAsk(false);
    setBusy(true);
    await save();
    setBusy(false);
  };

  /**
   * Wave 1B — a draft that totals 0.00 is ASKED about before it is saved: a
   * zero invoice is usually a line left at 0 or a price not yet typed, and
   * saving it silently filed a document nobody meant. A question, not a
   * refusal (posting one is still refused by `sales.errZeroTotal`), so a Yes
   * saves exactly what was typed. The mirror's `grandTotal` is null while a
   * row is unparsable, and an unparsable row is not a zero — it goes
   * straight to `validate()`'s own refusal.
   */
  const onSave = async () => {
    if (totals.grandTotal !== null && Money.of(totals.grandTotal, currency).isZero()) {
      setZeroAsk(true);
      return;
    }
    await saveNow();
  };

  /** Post = the Draft → Posted one-way door. Unsaved edits are saved first. */
  const onPost = async () => {
    // BR-MD-11-14, at the moment the rule names: "confirming a document in
    // that currency must warn: 'The newest EUR rate is from 2026-06-28.
    // Continue with 0.9180?'" — a question, not a refusal, so a No leaves the
    // draft exactly as it was. The server computes the sentence, because the
    // threshold and the rate it names are the server's facts.
    if (!(await confirmRateWarning(invoice?.rateWarning))) return;
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        const created = await save(false);
        if (!created) return;
        // The mutation hooks are bound to the URL's id, which just changed —
        // post the freshly created draft directly and refresh the caches.
        const response = await api.invoices.post({ params: { invoiceId: created.id }, body: {} });
        if (response.status !== 200) {
          if (response.status === 403) denyPermission('sales.approve');
          const body = response.body;
          const sentence =
            typeof body === 'object' && body !== null && 'message' in body
              ? String((body as { message: unknown }).message)
              : t('common.loadFailed');
          // D46: this door raises the stock family (serialsRequired,
          // batchRequired, insufficientStock…) as a matter of course, so the
          // body is lifted into an ApiError exactly the way throwApiError
          // lifts it — the seam then prefers the key+params it carries.
          const message = localizeServerMessage(
            t,
            sentence,
            new ApiError(response.status, sentence, body),
          );
          setError(message);
          // The draft EXISTS — `save(false)` created it before the refusal —
          // so the screen moves onto it, carrying the refusal in the router's
          // state so the sentence survives the remount. Staying on /sales/new
          // meant the next Post created a SECOND draft and abandoned the
          // first for ever. That was survivable while every refusal was a
          // typo; a tracked line is refused as a matter of course, exactly
          // once, until its serial is typed.
          void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
          void navigate(`/sales/${created.id}`, { replace: true, state: { postError: message } });
          return;
        }
        void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
        void navigate(`/sales/${created.id}`, { replace: true });
        return;
      }
      const saved = await save();
      if (!saved) return;
      const posted = await postInvoice.mutateAsync();
      setWarnings(posted.warnings ?? []);
    } catch (thrown) {
      handleFailure(thrown, 'sales.approve');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!invoiceId) return;
    setDeleting(false);
    setBusy(true);
    setError(null);
    try {
      await deleteInvoice.mutateAsync();
      void navigate('/sales');
    } catch (thrown) {
      handleFailure(thrown, 'sales.delete');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Ruling A6 / GEN-72: cancelling a POSTED invoice asks WHY — the ReasonBox
   * IS the confirm (ADM-02 §8: no browser chrome), and the typed string rides
   * the wire. Refused server-side while a receipt is allocated ("unmatch it
   * first") or a credit note stands (GEN-74, named), so this handler does not
   * pre-check either: a client that decided for itself would be a second
   * opinion about a fact only the server can see.
   */
  const onCancelPosted = async (reason: string) => {
    if (!invoiceId) return;
    setCancelAsk(false);
    setBusy(true);
    setError(null);
    try {
      await cancelInvoice.mutateAsync(reason);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'sales.approve');
    } finally {
      setBusy(false);
    }
  };

  /** The one legal edit on a posted document: its notes (BR-SAL-00-22). */
  const onSaveNotes = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateInvoice.mutateAsync({ notes: notes.trim() === '' ? null : notes });
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'sales.edit');
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loadingInvoice) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  const balanceDue = invoice?.balanceDue ?? totals.grandTotal;
  const fullyPaid = invoice?.fullyPaid ?? false;

  return (
    <div className="grid gap-4">
      {/* BR-MD-11-14's question in ADM-02 §8's shared box. Rendered
          UNCONDITIONALLY, unlike the delete box below: this one is awaited, so
          a dialog that unmounted mid-question would leave the post hanging with
          no error anywhere. See `useRateWarningGate`. */}
      {rateWarningDialog}
      {/* ── Toolbar: identity, lifecycle chip, actions ─────────────────── */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('sales.editorTitle')}</h2>
        {/* BR-SAL-00-02's naming half lives on the shared chip — this screen's
            fixed label is "Invoice No.", the list column's own string. */}
        <DocumentNumberChip
          label={t('sales.colNo')}
          code={invoice?.code ?? null}
          fallback={t('sales.codeNew')}
        />
        {/* BR-SAL-00-26 — "every one of the five documents must show its
            lifecycle status … on screen at all times". Unconditional: no
            status value, permission or loading state removes it, and the row
            it sits in is the sticky one GEN-36 pins, so "at all times" holds
            through the scroll as well as through the render. */}
        <DocStatusChip status={status} />
        {/* G-11 — the per-customer reference "<customer code>-<n>", allotted
            at posting, beside the legal number and never instead of it. The
            label is read, not seen, exactly as the number chip's is. */}
        {invoice?.partnerRef ? (
          <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
            <span className="sr-only">{t('sales.partnerRefLabel')} </span>
            <bdi dir="ltr">{invoice.partnerRef}</bdi>
          </span>
        ) : null}
        {/* The toolbar's acts, DECLARED. `document-toolbar.tsx` says at length
            why, and why `visible` (the document's question — BR-PERM-02) and
            `gate` (the role's — 16.7 row 2) are two fields and not one. The
            order below is the order this screen already drew, which is NOT
            PART 4.6's table order; aligning them is a decision for the owner,
            recorded in the primitive's header rather than taken quietly. */}
        <DocumentToolbar
          header={{ kind: 'invoice', id: invoiceId, dirty }}
          actions={[
            {
              id: 'back',
              // NOT PART 4.6's Cancel row. That one "marks a confirmed record
              // as no longer valid, and undoes its accounting"; this is a link
              // back to the list wearing the same word. See the primitive's
              // header — one label, two acts, is GEN-55's own problem and no rule-coverage:ignore
              // screen can fix it alone.
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/sales',
            },
            {
              // SAL-00 §5a's Print button, and DECISION.md §2.13's PDF beside
              // it. Both render the SAME document (packages/documents): the
              // browser prints it here, the worker turns it into a file. A
              // DRAFT prints stamped DRAFT rather than being refused — people
              // proof-read from paper — which is a rule the template owns, not
              // this screen. The declaration's one escape: see `NodeAction`.
              id: 'printPdf',
              row: 'print',
              node:
                !isNew && invoice ? (
                  <DocumentActions
                    request={{ kind: 'sales_invoice', locale: printLocale, invoiceId: invoice.id }}
                    payload={{ kind: 'sales_invoice', data: invoiceDocument(invoice, warehouses) }}
                  />
                ) : null,
            },
            {
              // SAL-05 §3: "Credit this invoice" — opens the Sales Return
              // screen with this invoice already linked and its lines loaded.
              // SAL-00 §5c "Record payment" — the receipt-voucher entry point,
              // the customer-side mirror of the bill's "Record payment". Opens
              // FIN-03's receipt with the customer chosen and this invoice
              // pre-allocated. Posted and not yet fully paid.
              id: 'recordPayment',
              row: null,
              label: t('sales.recordPayment'),
              icon: <CreditCard aria-hidden />,
              visible: status === 'posted' && !fullyPaid,
              to: `/accounting/receipts/new?partnerId=${invoice?.partnerId ?? ''}&invoiceId=${invoiceId}&currency=${invoice?.currency ?? ''}`,
            },
            {
              // Posted only (BR-SAL-05-02) and behind sales.create (it starts
              // a new draft document).
              id: 'credit',
              row: null,
              label: t('sales.creditThisInvoice'),
              icon: <Undo2 aria-hidden />,
              variant: 'outline',
              visible: status === 'posted',
              gate: { permission: 'sales.create', reasonKey: 'perm.cannot.createSalesReturns' },
              to: `/sales/returns/new?invoiceId=${invoiceId}`,
            },
            {
              // PART 4.6's Cancel row proper, and ruling A6's whole point:
              // "marks a confirmed record as no longer valid, and undoes its
              // accounting" — as opposed to the `back` link three acts above,
              // which wears the same word and only navigates. Posted only
              // (GEN-70: a draft deletes), and gated exactly like Post,
              // because cancelling undoes a posting and that is the same
              // authority read backwards.
              id: 'cancelPosted',
              row: 'cancel',
              label: t('sales.cancelInvoice'),
              icon: <X aria-hidden />,
              variant: 'destructive',
              visible: status === 'posted',
              gate: { permission: 'sales.approve', reasonKey: 'perm.cannot.cancelInvoices' },
              disabled: busy,
              onAct: () => setCancelAsk(true),
            },
            {
              // INV-H7 — a NEW invoice may start from another document's
              // lines. A pure copy (see `copy-from-modal.tsx`): the existing
              // quote → order → invoice conversions, which LINK, are elsewhere.
              id: 'copyFrom',
              row: null,
              label: t('sales.copyFrom'),
              icon: <Copy aria-hidden />,
              variant: 'outline',
              visible: isNew,
              gate: { permission: 'sales.create', reasonKey: 'perm.cannot.createInvoices' },
              onAct: () => setCopyOpen(true),
            },
            // INV-H1 + wave 4C — Import lines / Download template.
            ...lineImport.acts,
            {
              // Whether Delete EXISTS is the document's question — a posted
              // invoice has none for anybody, Owner included (BR-PERM-02).
              // Whether it is USABLE is the role's. 16.7 keeps them apart, and
              // so do these two fields.
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              // PART 4.6 draws Delete white and outlined. Every editor draws
              // it red, and this refactor changed no pixel — see the header.
              variant: 'destructive',
              visible: !isNew && isDraft,
              gate: { permission: 'sales.delete', reasonKey: 'perm.cannot.deleteInvoices' },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: isDraft,
              gate: {
                permission: isNew ? 'sales.create' : 'sales.edit',
                reasonKey: isNew ? 'perm.cannot.createInvoices' : 'perm.cannot.editInvoices',
              },
              busy,
              onAct: () => void onSave(),
            },
          ]}
        >
          {/* INV-H7's picker, inside the same guard as its button. */}
          {isNew && copyOpen ? <CopyFromModal onClose={() => setCopyOpen(false)} onPick={onCopyFrom} /> : null}
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && isDraft ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('sales.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          {/* Wave 1B — the zero-total question (ADM-02 §8's box, GEN-206's verb). */}
          <ConfirmBox
            open={zeroAsk}
            title={t('sales.zeroTotalTitle')}
            body={t('sales.zeroTotalBody')}
            confirmLabel={t('sales.zeroTotalConfirm')}
            onConfirm={() => void saveNow()}
            onCancel={() => setZeroAsk(false)}
            busy={busy}
          />
          {/* GEN-59's typed-reason box, inside the same guard as the button so
              the dialog cannot outlive the state that allows the act.

              THE PROMPT CARRIES BR-SAL-04-11's OWN SENTENCE when the tax
              authority has ACCEPTED this invoice — verbatim, including its
              question mark, and it is the FIRST thing the sentence says
              because it is the fact that changes the decision. ERR-SAL-04-04's
              "What the user should do" column is "Confirm, or raise a credit
              note instead", so this is a warning and not a wall: both roads
              stay open and Credit this invoice is two buttons away.

              It is composed rather than substituted because GEN-72's own
              promise — that the reason is written to the audit log — is true
              in both cases and dropping it to make room would trade one rule
              for another. */}
          {status === 'posted' ? (
            <ReasonBox
              open={cancelAsk}
              title={t('sales.cancelInvoice')}
              prompt={
                authorityAccepted
                  ? `${localizeServerMessage(t, SALES_MESSAGES.authorityAccepted)} ${t('sales.cancelPrompt')}`
                  : t('sales.cancelPrompt')
              }
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={busy}
              onConfirm={(reason) => void onCancelPosted(reason)}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
        </DocumentToolbar>
      </div>

      {/* Wave 4D — a posted cash invoice names the receipt that paid it. */}
      {status !== 'draft' && invoice?.isCash ? (
        <CashInvoiceBanner receiptId={invoice.cashReceiptId} receiptCode={invoice.cashReceiptCode} />
      ) : null}
      {/* BR-SAL-00-22's sentence, drawn as the locked notice. */}
      {status === 'posted' ? (
        <p className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          {t('sales.postedNotice')}
        </p>
      ) : null}
      {status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50/80 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {/* GEN-59's stored reason, read back OFF THE RECORD — the receipt's
              and the order's own shape. The null fallback is not defensive
              padding: it is the only sentence an invoice cancelled before
              ruling A6 could carry, and there are none of those, so it is
              there for the case where the column is legitimately empty rather
              than for a case anybody expects. */}
          {invoice?.cancelReason != null
            ? t('sales.cancelledReasonNotice', { reason: invoice.cancelReason })
            : t('sales.cancelledNotice')}
        </p>
      ) : null}
      <OverStockStrip show={anyShort} />
      {/* G-10 — possible duplicate, said once per write and never refused.
          The server's fixed sentence is matched to this screen's key (the
          bill editor's `bills.warnDuplicate*` shape), so an Arabic reader
          gets Arabic; an unknown sentence still shows rather than vanishes. */}
      {warnings.map((warning) => (
        <p
          key={warning}
          role="status"
          className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          <TriangleAlert className="size-4 shrink-0" aria-hidden />
          <span>
            {warning === SALES_MESSAGES.duplicateInvoiceWarning
              ? t('sales.warnDuplicateInvoice')
              : localizeServerMessage(t, warning)}
          </span>
        </p>
      ))}
      {error ? (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {/* INV-H1 — the import's chooser, summary and per-row problems. */}
      {lineImport.panel}
      {/* INV-H7 — what the last copy did, in one sentence. */}
      {importNote ? (
        <p role="status" className="text-sm text-muted-foreground">
          {importNote}
        </p>
      ) : null}

      {/* ── The document details line (SAL-00 §5c) — foldable (2026-10-30) ── */}
      <CollapsibleHeaderCard
        storageKey="salesInvoice"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        summary={{
          partner: invoice?.partnerName ?? partner?.name,
          date: invoiceDate,
          currency,
          code: invoice?.code,
          status: <DocStatusChip status={status} />,
        }}
      >
          <div className="grid gap-1.5">
            <Label htmlFor="inv-date">{t('common.date')}</Label>
            <Input
              id="inv-date"
              type="date"
              dir="ltr"
              value={invoiceDate}
              onChange={(event) => setInvoiceDate(event.target.value)}
              disabled={!editable}
            />
          </div>
          {/* MD-09 §9: with the Payment terms switch off, "the Due date
              field does not appear on a document" — it IS the document date. */}
          {paymentTermsOn ? (
            <div className="grid gap-1.5">
              <Label htmlFor="inv-due">{t('sales.fDueDate')}</Label>
              {/* Left empty, the server fills it from the customer's payment
                  terms (BR-MD-01-11, BR-MD-09-03) — editable while Draft. */}
              <Input
                id="inv-due"
                type="date"
                dir="ltr"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
                disabled={!editable}
              />
            </div>
          ) : null}
          <div className="grid gap-1.5">
            <Label htmlFor="inv-customer">{t('sales.fCustomer')}</Label>
            {/* GEN-50, and BR-MD-01-07: the picker OFFERS active customers
                only — while still DISPLAYING one that was switched off after
                this invoice was written. */}
            <PartnerTypeahead
              entity="customer"
              id="inv-customer"
              label={t('sales.fCustomer')}
              value={partnerId || null}
              onChange={(id) => setPartnerId(id ?? '')}
              options={customerPicker.options}
              selected={customerPicker.optionFor(partnerId)}
              query={customerPicker.query}
              onQueryChange={customerPicker.setQuery}
              placeholder={t('sales.pickCustomer')}
              emptyLabel={t('picker.noMatchingCustomer')}
              loading={customerPicker.loading}
              disabled={!editable || !isNew}
            />
            {/* Wave 4B — the customer's balance and credit bar under the
                customer: the server's own figures, drawn and never decided here. */}
            <PartnerBalanceChip partner={partner} className="justify-self-start" />
          </div>
          <div className="grid gap-1.5">
            <Label>{t('sales.fCustomerId')}</Label>
            <div className="flex h-12 items-center gap-3 rounded-[11px] border border-input bg-muted/40 px-3 font-mono text-sm">
              {/* BR-SAL-00-01 — "every field on a document screen must be
                  bound to the record on screen. No field may hold a value
                  belonging to another document." This is the field §6.7 names
                  as the prototype's worst offender: it held the literal
                  `C-1042`, a code belonging to no record at all. It reads the
                  OPEN INVOICE's own stored `partnerCode` first, so a saved
                  document shows the code it was written with even after the
                  picker has moved on, and falls back to the picked partner's
                  own code on a new draft. There is no literal on this path. */}
              <Ltr value={invoice?.partnerCode ?? partner?.code ?? '—'} />
              <span className="ms-auto text-muted-foreground">
                <Ltr value={currency} />
              </span>
            </div>
          </div>
          {/* INV-F1…F6 + INV-F5: the shared sales header, the FxPanel now in
              the grid's own empty space rather than under the totals. */}
          <SalesHeaderFields
            idPrefix="inv"
            partnerId={partnerId}
            state={header}
            onChange={(patch) => setHeader((current) => ({ ...current, ...patch }))}
            disabled={!editable}
            doc={invoice}
            rate={rate}
            commission={commission}
            currency={currency}
            currencyPicker={{ onChange: docCurrency.pick, disabled: currencyLocked }}
            warehouses={warehouses}
          />
          <CashInvoiceFields
            state={cash}
            onChange={(patch) => setCash((current) => ({ ...current, ...patch }))}
            disabled={!editable}
          />
      </CollapsibleHeaderCard>

      {/* ── SAL-04 §5: the E-invoicing & compliance strip, between the
             details line and the item grid (§4). It is what makes a sales
             invoice different from a purchase order, and it reads its status
             LIVE from the E-Invoicing module — never from a column on this
             document (BR-SAL-04-05). ─────────────────────────────────────── */}
      {invoiceId ? <ComplianceStrip invoiceId={invoiceId} posted={status !== 'draft'} /> : null}

      {/* ── The line-items grid (SAL-00 §5d, the contract's columns) ───── */}
      <Card>
        <CardContent>
          <Table ref={gridRef as React.Ref<HTMLTableElement>}>
            <TableHeader>
              <TableRow>
                <TableHead className="w-11 text-center">#</TableHead>
                <TableHead className="min-w-[150px]">{t('quotes.colItem')}</TableHead>
                <TableHead className="min-w-[130px]">{t('orders.colWarehouse')}</TableHead>
                <TableHead className="w-24">{t('sales.colUnit')}</TableHead>
                <TableHead className="min-w-[200px]">{t('sales.colDescription')}
                  {editable ? <DescriptionLangToggle language={descLang} /> : null}
                </TableHead>
                <TableHead className="w-24 text-end">{t('sales.colQty')}</TableHead>
                {bonusOn ? <TableHead className="w-24 text-end">{t('sales.colBonus')}</TableHead> : null}
                <TableHead className="w-32 text-end">{t('sales.colUnitPrice')}</TableHead>
                {discountOn ? <TableHead className="w-28 text-end">{t('sales.colDisc')}</TableHead> : null}
                <TableHead className="w-32 text-end">{t('sales.colTotal')}</TableHead>
                {editable ? <TableHead className="w-11" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line, index) => {
                const view = totals.lines[index];
                const blocks = blocksFor(line);
                return (
                  <React.Fragment key={line.key}>
                  <TableRow className="odd:bg-muted/30">
                    <TableCell className="text-center font-mono text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      {/* BR-SAL-00-24 — "the item-code search button must open
                          an item lookup. In the prototype it inserts a blank
                          row instead." The defect is gone and the requirement
                          is met, THROUGH A DIFFERENT CONTROL: the Code column
                          is itself the lookup — `ItemCell` is a typeahead over
                          the item catalogue — rather than a plain cell beside a
                          `search` button. Nothing on this row inserts a blank
                          line; the only control that adds one is the footer's
                          Add line. That the spec's button became a combobox is
                          a shape deviation and is recorded as one. */}
                      <ItemCell
                        label={t('quotes.cellItem', { line: index + 1 })}
                        clearLabel={t('quotes.freeTextLine')}
                        picker={itemPicker}
                        value={line.itemId}
                        onPick={(itemId) => pickItem(line.key, itemId)}
                        disabled={!editable}
                      />
                      {/* Wave 4C — the customer's last price for this item, one click to use. */}
                      {partnerId && line.itemId ? (
                        <LastPriceHint
                          side="sales"
                          partnerId={partnerId}
                          itemId={line.itemId}
                          currency={currency}
                          currentPrice={line.unitPrice}
                          editable={editable}
                          line={index + 1}
                          onUse={(price) => setLine(line.key, { unitPrice: price, typedUnitPrice: true })}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {line.itemId ? (
                        <NativeSelect
                          aria-label={t('orders.cellWarehouse', { line: index + 1 })}
                          className="h-9 text-sm"
                          value={line.warehouseId}
                          onChange={(event) => setLine(line.key, { warehouseId: event.target.value })}
                          disabled={!editable}
                        >
                          {/* INV-L3: empty = the header's default warehouse. */}
                          <option value="">
                            {header.warehouseId
                              ? (activeWarehouses.find((w) => w.id === header.warehouseId)?.name ??
                                t('sales.pickWarehouse'))
                              : t('sales.pickWarehouse')}
                          </option>
                          {activeWarehouses.map((warehouse) => (
                            <option key={warehouse.id} value={warehouse.id}>
                              {warehouse.name}
                            </option>
                          ))}
                        </NativeSelect>
                      ) : (
                        <span className="px-2 text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* INV-L4: the item's sell units; empty = its stock unit. */}
                      <LineUnitSelect
                        item={line.itemId ? itemPicker.byId.get(line.itemId) : undefined}
                        value={line.unitId}
                        // A new unit orphans the stored conversion, like a retyped qty.
                        onChange={(unitId) => setLine(line.key, { unitId, basePair: null })}
                        disabled={!editable}
                        label={t('sales.cellUnit', { line: index + 1 })}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        aria-label={t('sales.cellDescription', { line: index + 1 })}
                        value={line.description}
                        data-row={index}
                        data-col={0}
                        onKeyDown={(event) => onCellKeyDown(event, index, 0)}
                        onChange={(event) =>
                          setLine(line.key, {
                            description: event.target.value,
                            typedDescription: true,
                          })
                        }
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        mono
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('sales.cellQty', { line: index + 1 })}
                        value={line.qty}
                        data-row={index}
                        data-col={1}
                        onKeyDown={(event) => onCellKeyDown(event, index, 1)}
                        onChange={(event) =>
                          // Retyping the qty orphans the stored conversion, so
                          // the twin goes with it (see GridLine.basePair).
                          setLine(line.key, { qty: event.target.value, basePair: null })
                        }
                        disabled={!editable}
                      />
                      <LineStockHint available={lineShort(line)} />
                      {line.basePair ? (
                        /*
                         * BR-MD-12-10 / GEN-120 — "must show both on the line:
                         * '2 BOX (1,000 PCS)'". The box half is the input
                         * above (the line's own unit — BR-MD-03-04d); this is
                         * the stock-unit half, drawn only when the two units
                         * differ. One LTR island for the whole parenthesised
                         * pair, parens included: they are bidi-neutral, and
                         * left outside the island an RTL paragraph may put
                         * them on the wrong side of their own figure (the
                         * same reasoning as <Qty>).
                         */
                        <bdi
                          dir="ltr"
                          className="block px-2 text-end font-mono text-xs tabular-nums text-muted-foreground"
                        >
                          (
                          {formatQtyWithUnit(
                            line.basePair.qty,
                            line.basePair.precision,
                            line.basePair.symbol,
                          )}
                          )
                        </bdi>
                      ) : null}
                    </TableCell>
                    {bonusOn ? (
                      <TableCell>
                        {/* INV-L1: free units, same warehouse and unit as the
                            line; they leave stock at posting at no charge. */}
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellBonus', { line: index + 1 })}
                          value={line.bonusQty}
                          placeholder="0"
                          onChange={(event) => setLine(line.key, { bonusQty: event.target.value })}
                          disabled={!editable}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <CellInput
                        mono
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('sales.cellUnitPrice', { line: index + 1 })}
                        value={line.unitPrice}
                        data-row={index}
                        data-col={2}
                        onKeyDown={(event) => onCellKeyDown(event, index, 2)}
                        onChange={(event) =>
                          setLine(line.key, {
                            unitPrice: event.target.value,
                            typedUnitPrice: true,
                          })
                        }
                        disabled={!editable}
                      />
                      <BelowCostHint cost={lineBelowCost(line)} />
                    </TableCell>
                    {discountOn ? (
                      <TableCell>
                        {/* L7 — the only two-line cell: the % box over the money
                            it takes off. INV-L2: drawn only while the company's
                            switch is on; off, the line saves with no discount. */}
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellDisc', { line: index + 1 })}
                          value={line.discountPct}
                          placeholder="0"
                          data-row={index}
                          data-col={3}
                          onKeyDown={(event) => onCellKeyDown(event, index, 3)}
                          onChange={(event) => setLine(line.key, { discountPct: event.target.value })}
                          disabled={!editable}
                        />
                        {view?.discountAmount && view.discountAmount !== '0.00' ? (
                          <div className="pe-2 text-end text-xs text-muted-foreground">
                            <bdi dir="ltr">−{view.discountAmount}</bdi>
                          </div>
                        ) : null}
                      </TableCell>
                    ) : null}
                    <TableCell className="text-end">
                      {view?.lineTotal !== null && view?.lineTotal !== undefined ? (
                        <Amount value={view.lineTotal} currency={currency} />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    {editable ? (
                      <TableCell>
                        <button
                          type="button"
                          aria-label={t('sales.removeLine', { line: index + 1 })}
                          title={t('sales.removeLine', { line: index + 1 })}
                          onClick={() => removeLine(line.key)}
                          className="flex size-8 items-center justify-center rounded-md text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <X className="size-4" aria-hidden />
                        </button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                  {/* OQ-05-17: the sub-table expands UNDER the line it belongs
                      to, one block per thing that has to be identified. */}
                  <TrackingRows
                    blocks={blocks}
                    text={line.tracking}
                    onChange={(blockKey, value) => setTracking(line.key, blockKey, value)}
                    disabled={!editable}
                    columns={8 + (bonusOn ? 1 : 0) + (discountOn ? 1 : 0) + (editable ? 1 : 0)}
                    line={index + 1}
                  />
                  </React.Fragment>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={5}>
                  {editable ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-dashed"
                      onClick={() => setLines((current) => [...current, blankLine()])}
                    >
                      <Plus aria-hidden /> {t('sales.addLine')}
                    </Button>
                  ) : null}
                </TableCell>
                <TableCell className="text-end">
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('sales.qtyTotal')}
                  </span>{' '}
                  <bdi dir="ltr" className="font-mono font-semibold">
                    {totals.qtyTotal}
                  </bdi>
                </TableCell>
                <TableCell
                  colSpan={2 + (bonusOn ? 1 : 0) + (discountOn ? 1 : 0) + (editable ? 1 : 0)}
                  className="text-end"
                >
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('sales.subtotal')}
                  </span>{' '}
                  <Amount value={totals.subtotal} currency={currency} className="font-semibold" />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
          {editable ? (
            <p className="mt-2 text-xs text-muted-foreground">{t('sales.gridHelp')}</p>
          ) : null}
        </CardContent>
      </Card>

      {/* ── Notes + summary: fluid column and the pinned rail, stacking on
             narrow screens (SAL-00 §4's proposed fix). ─────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader>
            <CardTitle>{t('sales.notes')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Textarea
              aria-label={t('sales.notes')}
              placeholder={t('sales.notesPh')}
              maxLength={2000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={!editable && !(status === 'posted' && canEditNotes)}
            />
            {status === 'posted' ? (
              <Gated
                permission="sales.edit"
                reasonKey="perm.cannot.editNotes"
                className="justify-self-start"
              >
                <Button variant="outline" onClick={() => void onSaveNotes()} disabled={busy}>
                  {t('sales.saveNotes')}
                </Button>
              </Gated>
            ) : null}
          </CardContent>
        </Card>

        {/* SAL-00 §5f — the totals summary. */}
        <Card>
          <CardContent className="grid gap-3">
            {/* Wave 4C — the shared totals rows (F-2…F-7), with the currency code by the total. */}
            <DocumentTotals
              currency={currency}
              subtotal={totals.subtotal}
              discountAmount={totals.discountAmount}
              discountControl={
                <TwoWayPair
                  kind="discount"
                  state={discount}
                  onChange={setDiscount}
                  pct={totals.discountPct ?? '0.00'}
                  amount={totals.discountAmount ?? ''}
                  disabled={!editable}
                />
              }
              taxAmount={totals.taxAmount}
              vatControl={
                <TwoWayPair
                  kind="vat"
                  state={tax}
                  onChange={setTax}
                  pct={totals.taxPct ?? defaultTaxPct}
                  amount={totals.taxAmount ?? ''}
                  disabled={!editable}
                />
              }
              grandTotal={totals.grandTotal}
            />

            {/* BR-SAL-04-09's "as of" date is drawn by the FxPanel, which
                INV-F5 moved up into the header grid's Currency & rate cell
                (SalesHeaderFields) — one place, beside the currency. */}

            {/* Wave 4B — the invoice's margin, for a reader allowed to see cost. */}
            <MarginPanel
              lines={lines}
              items={itemPicker.byId}
              net={netAmount(totals.subtotal, totals.discountAmount, currency)}
              currency={currency}
            />

            <div className="flex items-center justify-between text-sm">
              <span>{t('sales.amountPaid')}</span>
              <Amount value={invoice?.amountPaid ?? '0.00'} currency={currency} />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>{t('sales.balanceDue')}</span>
              {/* Green at or below the 0.0001 tolerance, amber otherwise. */}
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 font-semibold',
                  fullyPaid
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
                )}
              >
                {balanceDue !== null ? <Amount value={balanceDue} currency={currency} /> : '—'}
              </span>
            </div>

            {/* T9 — Post invoice, behind sales.approve (BR-SAL-04-10). Greyed
                rather than hidden: the greying is UX, the server enforces. */}
            {isDraft ? (
              <Gated
                permission="sales.approve"
                reasonKey="perm.cannot.postInvoices"
                className="w-full"
              >
                <Button
                  className="h-[46px] w-full bg-emerald-700 text-white hover:bg-emerald-800"
                  onClick={() => void onPost()}
                  disabled={busy || !partnerId || usedLines.length === 0}
                >
                  {busy ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
                  {t('sales.postInvoice')}
                </Button>
              </Gated>
            ) : null}
            {isDraft && !canPost ? (
              // BR-SAL-04-10 / ERR-SAL-04-05's amber banner for the
              // permission-less reader. The sentence says the invoice HAS BEEN
              // SENT to an approver, which is the rule's own wording and what
              // the purchase strip already said — it read "posting is for an
              // approver" until the ERR sweep, which answers a different
              // question than the one a refused user is asking. Pinned in
              // spec-wording.spec.ts, both locales.
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('sales.approvalBanner')}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* INV-H5 — files on the invoice (a scan, a signed copy). Hidden
          behind "save first" on a new draft; permission follows sales.* */}
      <AttachmentsPanel kind="invoice" entityId={invoiceId} />
    </div>
  );
}
